/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Tue Jun 27 2023
 **/

import { Badge, Button } from "@/frameworks/designStuff/components";
import { AvatarGroup } from "@/frameworks/designStuff/compounds";
import { useChatRoomActions } from "@/frameworks/core/chat";
import { Avatar as CoreAvatar } from "@/frameworks/core/components";
import { MinimizeChatItemType, useMinimizeChatList } from "@/frameworks/core/hooks";
import { Maybe } from "@/frameworks/libraries/components";

import { S, SpaceChatAvatar } from ".";
import { Configure } from "@/frameworks/libraries/utilities";

export default function MinimizeChatItem(minimizeChat: MinimizeChatItemType) {
	const chatRoomActions = useChatRoomActions();
	const { minimizeChatListActions } = useMinimizeChatList();
	const handleClickMinimizeChatItem = () => {
		if (minimizeChat.type === "DM") {
			chatRoomActions.common.open({ channelID: minimizeChat.id, type: "DM", size: minimizeChat?.size });
		} else {
			chatRoomActions.common.open({
				channelID: minimizeChat.id,
				type: "Space",
				spaceID: minimizeChat.spaceID || "",
				size: minimizeChat?.size
			});
		}
		handleRemoveItem();
	};

	const handleRemoveItem = () => {
		minimizeChatListActions.remove(minimizeChat.id);
	};

	return (
		<S.ListWrapper>
			{minimizeChat.type === "Space" ? (
				<SpaceChatAvatar
					roomID={minimizeChat.id}
					spaceID={minimizeChat.spaceID}
					onClick={handleClickMinimizeChatItem}
				/>
			) : (
				<AvatarGroup
					key={minimizeChat.id}
					size="48px"
					avatarList={
						minimizeChat.users
							?.filter((user) => {
								if (minimizeChat.users.length === 1) return user;
								return user.userID !== Configure.userID;
							})
							.map((user) => {
								return (
									<CoreAvatar
										showMiniProfile={false}
										key={user.userID}
										kind="people_48"
										id={user.userID}
										useStatus={false}
									/>
								);
							}) || []
					}
					onClick={handleClickMinimizeChatItem}
				/>
			)}

			<Maybe test={minimizeChat.new}>
				<S.NewMessageDotBadge>
					<Badge type="xlargeDot" />
				</S.NewMessageDotBadge>
			</Maybe>

			<S.RemoveMinimizeChatItemBadge className="close">
				<Button.Icon
					name="Close24"
					size="small"
					shape="round"
					filled="error"
					fill="neutral_00_100"
					onClick={handleRemoveItem}
				/>
			</S.RemoveMinimizeChatItemBadge>
		</S.ListWrapper>
	);
}
